import { musicianServices } from "./pricingData";

export interface EnsemblePackage {
  ensembleSize: 2 | 3 | 4 | 5 | 6;
  ensembleName: string;
  name: string;
  musicians: string[];
  moment: string;
  description: string;
  basePrice: number;
  duration: string;
  guestCountMin: number;
  guestCountMax: number;
  features: string[];
}

// Regional manager buffer and margins (matching packageTiers.ts)
const TOTAL_RM_BUFFER = 860;
const MUSICIAN_MEAL_PER_DATE = 15;
const MUSICIAN_MARGIN = 0.0;
const BUFFER_MARGIN = 0.25;

// Get musician cost
function getMusicianCost(musicianName: string): number {
  const musician = musicianServices.find(m => m.name === musicianName);
  return musician ? musician.rollup4Hr : 4500; // Default fallback
}

// Fixed price ladder by ensemble size - ensures clear separation
const ENSEMBLE_BASE_PRICES: Record<number, number> = {
  2: 4000,   // Duo
  3: 5500,   // Trio
  4: 7000,   // Quartet
  5: 8500,   // Quintet
  6: 10000,  // Sextet
};

// Calculate package price based on number of musicians
function calculateEnsemblePrice(musicians: string[]): number {
  const ensembleSize = musicians.length;
  
  // Use fixed base price from ladder
  const basePrice = ENSEMBLE_BASE_PRICES[ensembleSize] || 7000;
  
  // Add small variation based on actual instruments (+/- $500 max)
  const musicianCosts = musicians.reduce((total, musicianName) => {
    return total + getMusicianCost(musicianName);
  }, 0);
  
  const avgMusicianCost = musicianCosts / ensembleSize;
  const costVariation = Math.min(Math.max((avgMusicianCost - 1625) * 0.3, -500), 500);
  
  const finalPrice = Math.round((basePrice + costVariation) / 100) * 100;
  
  return finalPrice;
}

// Map instrument names to musician service names
function mapInstrumentToMusician(instrument: string): string {
  const instrumentMap: Record<string, string> = {
    'violin': 'Violinists',
    'cello': 'Cellists',
    'viola': 'Viola Players',
    'harp': 'Harpists',
    'saxophone': 'Saxophonists',
    'piano': 'Pianists',
    'guitar': 'Acoustic Guitarists',
    'bass': 'Bass Players',
    'drums': 'Drummers',
    'trumpet': 'Trumpet Players',
    'trombone': 'Trombonists',
    'shehnai': 'Shehnai Players',
    'tabla': 'Tabla Players',
    'dhol': 'Dhol Players',
    'bansuri': 'Bansuri Players',
    'sitar': 'Indian Classical Strings',
    'flute': 'Bansuri Players',
  };

  const lowerInstrument = instrument.toLowerCase();
  for (const [key, value] of Object.entries(instrumentMap)) {
    if (lowerInstrument.includes(key) || key.includes(lowerInstrument)) {
      return value;
    }
  }
  
  // Return the instrument name as-is if no mapping found
  return instrument;
}

// Get ensemble size name
function getEnsembleSizeName(size: number): string {
  const sizeNames: Record<number, string> = {
    2: 'Duo',
    3: 'Trio',
    4: 'Quartet',
    5: 'Quintet',
    6: 'Sextet',
  };
  return sizeNames[size] || `${size} Musicians`;
}

/**
 * Generate AI-recommended ensemble packages for a specific moment
 * Creates packages for ensemble sizes: 2, 3, 4, 5, 6 musicians
 * Pricing strictly increases with ensemble size
 */
export function generateEnsemblePackages(
  moment: string,
  recommendedInstruments: string[],
  culturalContext: string = 'Mixed'
): EnsemblePackage[] {
  if (!recommendedInstruments || recommendedInstruments.length === 0) {
    // Fallback to default instruments based on moment
    if (moment.includes('Cocktail')) {
      recommendedInstruments = ['violin', 'cello', 'piano'];
    } else if (moment.includes('Ceremony')) {
      recommendedInstruments = ['violin', 'harp', 'cello'];
    } else {
      recommendedInstruments = ['guitar', 'piano', 'saxophone'];
    }
  }

  // Map instruments to musician names
  const availableMusicians = recommendedInstruments
    .map(inst => mapInstrumentToMusician(inst))
    .filter((value, index, self) => self.indexOf(value) === index); // Remove duplicates

  // Ensure we have enough musicians for a sextet (with safety limit to prevent infinite loops)
  const defaultMusicians = ['Violinists', 'Cellists', 'Pianists', 'Saxophonists', 'Acoustic Guitarists', 'Harpists'];
  let safetyCounter = 0;
  while (availableMusicians.length < 6 && safetyCounter < 20) {
    safetyCounter++;
    const nextDefault = defaultMusicians[availableMusicians.length % defaultMusicians.length];
    if (!availableMusicians.includes(nextDefault)) {
      availableMusicians.push(nextDefault);
    } else {
      // If duplicate, just add by index to break the loop
      availableMusicians.push(defaultMusicians[safetyCounter % defaultMusicians.length]);
    }
  }

  const packages: EnsemblePackage[] = [];
  const ensembleSizes = [2, 3, 4, 5, 6] as const;

  ensembleSizes.forEach(size => {
    const musicians = availableMusicians.slice(0, size);
    const ensembleName = getEnsembleSizeName(size);
    const basePrice = calculateEnsemblePrice(musicians);

    // Define ensemble-specific differentiators
    const differentiators: Record<number, string> = {
      2: 'Intimate Setting Specialists',
      3: 'Balanced Sound Coverage',
      4: 'Full Ensemble Experience',
      5: 'Premium Sound Layering',
      6: 'Maximum Sonic Impact',
    };

    packages.push({
      ensembleSize: size,
      ensembleName,
      name: `${moment} ${ensembleName}`,
      musicians,
      moment,
      description: `${size} professional musicians for ${moment.toLowerCase()} - AI recommended based on your event profile`,
      basePrice,
      duration: 'Up to 4 hours',
      guestCountMin: 0,
      guestCountMax: size === 2 ? 75 : size === 3 ? 150 : size === 4 ? 250 : size === 5 ? 350 : 500,
      features: [
        `${size} Professional Musicians`,
        differentiators[size] || 'Professional Ensemble',
        'Regional Manager On-Site',
        'Equipment & Sound Included',
        'All Travel & Meals Included',
        'Custom Repertoire Planning',
      ],
    });
  });

  // Verify pricing increases with size
  for (let i = 1; i < packages.length; i++) {
    if (packages[i].basePrice <= packages[i - 1].basePrice) {
      console.warn(`Price inconsistency detected for ${moment}: ${packages[i].ensembleSize} musicians (${packages[i].basePrice}) should cost more than ${packages[i-1].ensembleSize} musicians (${packages[i-1].basePrice})`);
    }
  }

  return packages;
}
